import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";

import { apiRoutes } from "../../../../configs/apiRoutes";
import instance from "../../../../api/apiClient";
import sanitizeFileName from "helpers/sanitizeFileName";
import { mergeExistingFields } from "helpers/mergeExistingFields";
import { ApiErrorPayload } from "types/ApiErrorPayload";
import {
  LoyaltyLevelReward,
  normalizeLoyaltyRewards,
  sortLoyaltyRewards,
} from "./rewards";

export interface LoyaltyLevelItem {
  id: number;
  levelNumber: string;
  title: string;
  description: string;
  pointsRequired: string;
  totalLevelUpAmount?: string | number | null;
  totalLevelUpCurrency?: string | null;
  valueFrom?: string;
  valueToComplete: string;
  image: string | File;
  mobileImage?: string | File;
  portraitImage?: string | File;
  removeDesktopImage?: boolean;
  removeMobileImage?: boolean;
  removePortraitImage?: boolean;
  isActive: boolean | number;
  locales?: string[];
  rewards?: LoyaltyLevelReward[];
}
export interface LoyaltyLevelBunchPayload {
  ids: number[];
  isActive: number;
}
export interface LoyaltyLevelTranslationPayload {
  locale: string;
  title: string;
  description?: string;
  isActive: number | boolean;
  content: string;
}

export interface LoyaltyLevelTranslationState {
  [locale: string]: LoyaltyLevelTranslationPayload;
}
interface LoyaltyLevelsState {
  rewardCurrencies: string[];
  list: LoyaltyLevelItem[];
  activePage: number;
  totalPages: number;
  totalItems: number;
  isListFetched: boolean;
  isItemFetched: boolean;
  selectedItems: number[];
  payload?: LoyaltyLevelItem;
  translations: LoyaltyLevelTranslationState;
  isItemFetching: boolean;
  processingItemId?: LoyaltyLevelItem["id"];
}
interface GetListPayload {
  limit: number;
  offset: number;
  title: string;
  level: string;
  isActive?: number;
  order: {
    isActive: string;
    title?: string;
    valueToComplete?: string;
    levelNumber?: string;
  };
}

export const getLoyaltyLevelsListList = createAsyncThunk(
  "loyalty-levels/list",
  async (data: GetListPayload) => {
    try {
      const result = await instance.post(`${apiRoutes.loyaltyLevels}?`, {
        ...data,
      });
      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);

export const getLoyaltyRewardCurrencies = createAsyncThunk(
  "loyalty-levels/reward-currencies",
  async () => {
    const result = await instance.post(apiRoutes.loyaltyLevels, {
      limit: 1,
      offset: 0,
    });
    return (result.data?.rewardCurrencies ?? []) as string[];
  },
);

export const updateLoyaltyLevelsBunch = createAsyncThunk(
  "loyalty-levels/update-bunch",
  async (data: LoyaltyLevelBunchPayload) => {
    try {
      await instance.put(`${apiRoutes.loyaltyLevelUpdateBunch}`, {
        ...data,
        isActive: data.isActive,
      });
      return true;
    } catch (error) {
      return false;
    }
  },
);

export const createLoyaltyLevel = createAsyncThunk<
  number,
  LoyaltyLevelItem,
  { rejectValue: ApiErrorPayload }
>(
  "loyalty-levels/create",
  async (payload: LoyaltyLevelItem, { rejectWithValue }) => {
    const data = new FormData();

    if (payload.image instanceof File) {
      const safeName = sanitizeFileName(payload.image.name);

      data.append(
        "image",
        new File([payload.image], safeName, { type: payload.image.type }),
      );
    }
    if (payload.mobileImage instanceof File) {
      const safeName = sanitizeFileName(payload.mobileImage.name);
      data.append(
        "mobileImage",
        new File([payload.mobileImage], safeName, {
          type: payload.mobileImage.type,
        }),
      );
    }
    if (payload.portraitImage instanceof File) {
      const safeName = sanitizeFileName(payload.portraitImage.name);
      data.append(
        "portraitImage",
        new File([payload.portraitImage], safeName, {
          type: payload.portraitImage.type,
        }),
      );
    }
    data.append("title", payload?.title ?? "");
    data.append("levelNumber", payload?.levelNumber?.toString() ?? "");
    data.append("pointsRequired", payload?.pointsRequired?.toString() ?? "");
    data.append(
      "totalLevelUpAmount",
      payload?.totalLevelUpAmount?.toString() ?? "0",
    );
    data.append("totalLevelUpCurrency", payload?.totalLevelUpCurrency ?? "");
    data.append("description", payload?.description ?? "");
    data.append("isActive", payload.isActive ? "1" : "0");

    try {
      const response = await instance.post(
        `${apiRoutes.loyaltyLevelCreate}`,
        data,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );
      return response.data.id;
    } catch (error: any) {
      const apiError = error?.data?.error;

      if (apiError?.code) {
        return rejectWithValue(apiError);
      }

      return rejectWithValue({
        code: "InternalError",
        fieldErrors: [],
        globalErrors: [{ code: "unknownError" }],
      });
    }
  },
);

export const updateLoyaltyLevel = createAsyncThunk<
  LoyaltyLevelItem,
  LoyaltyLevelItem,
  { rejectValue: ApiErrorPayload }
>(
  "loyalty-levels/update",
  async (payload: LoyaltyLevelItem, { rejectWithValue }) => {
    const data = new FormData();
    if (payload.image instanceof File) {
      const safeName = sanitizeFileName(payload.image.name);

      data.append(
        "image",
        new File([payload.image], safeName, { type: payload.image.type }),
      );
    }
    if (payload.mobileImage instanceof File) {
      const safeName = sanitizeFileName(payload.mobileImage.name);
      data.append(
        "mobileImage",
        new File([payload.mobileImage], safeName, {
          type: payload.mobileImage.type,
        }),
      );
    }
    if (payload.portraitImage instanceof File) {
      const safeName = sanitizeFileName(payload.portraitImage.name);
      data.append(
        "portraitImage",
        new File([payload.portraitImage], safeName, {
          type: payload.portraitImage.type,
        }),
      );
    }
    data.append("title", payload?.title ?? "");
    data.append("levelNumber", payload?.levelNumber?.toString() ?? "");
    data.append("pointsRequired", payload?.pointsRequired?.toString() ?? "");
    data.append(
      "totalLevelUpAmount",
      payload?.totalLevelUpAmount?.toString() ?? "0",
    );
    data.append("totalLevelUpCurrency", payload?.totalLevelUpCurrency ?? "");
    data.append("description", payload?.description ?? "");
    data.append("isActive", payload.isActive ? "1" : "0");
    data.append(
      "removeDesktopImage",
      payload.removeDesktopImage ? "true" : "false",
    );
    data.append(
      "removeMobileImage",
      payload.removeMobileImage ? "true" : "false",
    );
    data.append(
      "removePortraitImage",
      payload.removePortraitImage ? "true" : "false",
    );

    try {
      const result = await instance.put(
        `${apiRoutes.loyaltyLevelUpdate}/${payload.id}`,
        data,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );
      return {
        ...result.data,
        rewards: sortLoyaltyRewards(result.data?.rewards ?? []),
      };
    } catch (error: any) {
      const data = error?.data;
      const apiError = data?.error ?? data;

      if (apiError && apiError.code) {
        return rejectWithValue(apiError);
      }

      return rejectWithValue({
        code: "InternalError",
        fieldErrors: [],
        globalErrors: [{ code: "unknownError" }],
      });
    }
  },
);

export const deleteLoyaltyLevel = createAsyncThunk(
  "loyalty-levels/delete",
  async (id: string | number) => {
    try {
      await instance.delete(`${apiRoutes.loyaltyLevelDelete}/${id}`);
      return true;
    } catch (error) {
      return false;
    }
  },
);
export const getLevelItem = createAsyncThunk(
  "loyalty-levels/read",
  async (id: string | number) => {
    try {
      const result = await instance.get(`${apiRoutes.loyaltyLevelItem}/${id}`);
      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);

export const replaceLoyaltyLevelRewards = createAsyncThunk<
  LoyaltyLevelItem,
  { levelId: number; rewards: LoyaltyLevelReward[] },
  { rejectValue: ApiErrorPayload }
>(
  "loyalty-levels/replace-rewards",
  async ({ levelId, rewards }, { rejectWithValue }) => {
    try {
      await instance.put(
        `${apiRoutes.loyaltyLevelRewards}/${levelId}/rewards`,
        {
          rewards: normalizeLoyaltyRewards(rewards),
        },
      );
      const result = await instance.get(
        `${apiRoutes.loyaltyLevelItem}/${levelId}`,
      );

      return {
        ...result.data,
        rewards: sortLoyaltyRewards(result.data?.rewards ?? []),
      };
    } catch (error: any) {
      const data = error?.data;
      const apiError = data?.error ?? data;

      if (apiError?.code) {
        return rejectWithValue(apiError);
      }

      return rejectWithValue({
        code: "InternalError",
        fieldErrors: [],
        globalErrors: [{ code: "unknownError" }],
      });
    }
  },
);

export const setLoyaltyLevelTranslation = createAsyncThunk<
  boolean,
  { levelId: number; payload: LoyaltyLevelTranslationPayload },
  { rejectValue: ApiErrorPayload }
>(
  "loyalty-levels/set-translation",
  async (
    {
      levelId,
      payload,
    }: {
      levelId: number;
      payload: LoyaltyLevelTranslationPayload;
    },
    { rejectWithValue },
  ) => {
    try {
      await instance.put(
        `${apiRoutes.loyaltyLevelSetTranslations}/${levelId}`,
        payload,
      );
      return true;
    } catch (error: any) {
      const data = error?.data;
      const apiError = data?.error ?? data;

      if (apiError?.code) {
        return rejectWithValue(apiError);
      }

      return rejectWithValue({
        code: "InternalError",
        fieldErrors: [],
        globalErrors: [{ code: "unknownError" }],
      });
    }
  },
);

export const getLoyaltyLevelTranslation = createAsyncThunk(
  "loyalty-levels/get-translation",
  async ({ levelId, locale }: { levelId: number; locale: string }) => {
    try {
      const result = await instance.get(
        `${apiRoutes.loyaltyLevelGetTranslations}/${levelId}?locale=${locale}`,
      );
      return { locale, data: result.data };
    } catch (error) {
      throw error;
    }
  },
);

const initialState: LoyaltyLevelsState = {
  rewardCurrencies: [],
  list: [],
  isListFetched: false,
  isItemFetched: false,
  activePage: 1,
  totalItems: 0,
  totalPages: 0,
  selectedItems: [],
  translations: {},
  isItemFetching: false,
};

export const loyaltyLevelsSlice = createSlice({
  name: "loyalty-levels",
  initialState,
  reducers: {
    handlePaginate: (
      state: LoyaltyLevelsState,
      action: PayloadAction<number>,
    ) => {
      state.activePage = action.payload;
      state.isListFetched = false;
      state.selectedItems = [];
    },
    setTranslationLocally: (
      state: LoyaltyLevelsState,
      action: PayloadAction<{
        locale: string;
        data: LoyaltyLevelTranslationPayload;
      }>,
    ) => {
      const { locale, data } = action.payload;
      state.translations[locale] = data;
    },
    handleUpdateFields: (
      state: LoyaltyLevelsState,
      action: PayloadAction<Partial<LoyaltyLevelItem>>,
    ) => {
      state.payload = {
        ...state.payload,
        ...action.payload,
      } as LoyaltyLevelItem;
    },
    handleClearFields: (state: LoyaltyLevelsState) => {
      state.payload = undefined;
      state.isItemFetched = false;
    },
    handleSelectItem: (
      state: LoyaltyLevelsState,
      action: PayloadAction<number>,
    ) => {
      if (state.selectedItems.includes(action.payload)) {
        state.selectedItems = state.selectedItems.filter(
          (el) => el !== action.payload,
        );
      } else {
        state.selectedItems = [...state.selectedItems, action.payload];
      }
    },
    handleSelectAll: (state: LoyaltyLevelsState) => {
      if (state.selectedItems.length === state.list.length) {
        state.selectedItems = [];
      } else {
        state.selectedItems = state.list.map((el) => el.id ?? 0);
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getLoyaltyRewardCurrencies.fulfilled, (state, action) => {
      state.rewardCurrencies = action.payload;
    });
    builder.addCase(getLoyaltyRewardCurrencies.rejected, (state) => {
      state.rewardCurrencies = [];
    });
    builder.addCase(getLoyaltyLevelsListList.fulfilled, (state, action) => {
      state.isListFetched = true;
      state.list = action.payload?.items ?? [];
      state.rewardCurrencies = action.payload?.rewardCurrencies ?? [];
      state.totalItems = action.payload?.totalItems ?? 1;
      state.totalPages = action.payload?.totalPages ?? 1;
    });
    builder.addCase(createLoyaltyLevel.pending, (state) => {
      state.isItemFetching = true;
    });
    builder.addCase(createLoyaltyLevel.fulfilled, (state) => {
      state.isListFetched = false;
      state.isItemFetching = false;
    });
    builder.addCase(createLoyaltyLevel.rejected, (state) => {
      state.isItemFetching = false;
    });
    builder.addCase(updateLoyaltyLevel.pending, (state, action) => {
      state.isItemFetching = true;
      state.processingItemId = action.meta.arg.id;
    });
    builder.addCase(updateLoyaltyLevel.fulfilled, (state, action) => {
      state.list = state.list.map((item) =>
        item.id === action.meta.arg.id
          ? mergeExistingFields(item, action.payload)
          : item,
      );
      state.isItemFetching = false;
      state.processingItemId = undefined;
      state.processingItemId = undefined;
      state.payload = {
        ...action.payload,
        rewards: state.payload?.rewards ?? action.payload.rewards,
      };
    });
    builder.addCase(updateLoyaltyLevel.rejected, (state) => {
      state.isItemFetching = false;
      state.processingItemId = undefined;
      state.processingItemId = undefined;
    });
    builder.addCase(deleteLoyaltyLevel.pending, (state, action) => {
      state.processingItemId = +action.meta.arg || undefined;
    });
    builder.addCase(deleteLoyaltyLevel.fulfilled, (state) => {
      state.isListFetched = false;
      state.processingItemId = undefined;
    });
    builder.addCase(deleteLoyaltyLevel.rejected, (state) => {
      state.isListFetched = false;
      state.processingItemId = undefined;
    });
    builder.addCase(getLevelItem.fulfilled, (state, action) => {
      state.isItemFetched = true;
      state.payload = action.payload
        ? {
            ...action.payload,
            rewards: sortLoyaltyRewards(action.payload.rewards ?? []),
          }
        : undefined;
    });
    builder.addCase(replaceLoyaltyLevelRewards.pending, (state) => {
      state.isItemFetching = true;
    });
    builder.addCase(replaceLoyaltyLevelRewards.fulfilled, (state, action) => {
      state.isListFetched = false;
      state.isItemFetching = false;
      state.payload = action.payload;
    });
    builder.addCase(replaceLoyaltyLevelRewards.rejected, (state) => {
      state.isItemFetching = false;
    });
    builder.addCase(updateLoyaltyLevelsBunch.fulfilled, (state) => {
      state.isListFetched = false;
      state.selectedItems = [];
    });
    builder.addCase(getLoyaltyLevelTranslation.fulfilled, (state, action) => {
      const { locale, data } = action.payload;
      state.translations[locale] = data;
    });
    builder.addCase(setLoyaltyLevelTranslation.pending, (state) => {
      state.isItemFetching = true;
    });
    builder.addCase(setLoyaltyLevelTranslation.fulfilled, (state) => {
      state.isListFetched = false;
      state.isItemFetching = false;
    });
    builder.addCase(setLoyaltyLevelTranslation.rejected, (state) => {
      state.isItemFetching = false;
    });
  },
});

export const {
  handlePaginate,
  handleSelectItem,
  handleSelectAll,
  handleUpdateFields,
  handleClearFields,
  setTranslationLocally,
} = loyaltyLevelsSlice.actions;

// updateLoyaltyLevelsBunch is already exported above
